<script setup lang="ts">
  import { Typo, Button, TypoVariant, Illustration } from '@travelata-libs/ui-kit';
  import { CookieService, useViewport } from '@travelata-libs/utils';
  import { ref, onMounted, computed } from 'vue';

  interface CookieConsentProps {
    cookieName?: string;
    cookieValue?: string;
    cookiesUrl?: string;
    recommendationTechnologyUrl?: string;
    privacyPolicyUrl?: string;
    personalDataAgreementUrl?: string;
    domain?: string;
    image?: boolean;
  }

  const props = withDefaults(defineProps<CookieConsentProps>(), {
    cookieName: 'cookie_consent_accepted',
    cookieValue: 'true',
    cookiesUrl: 'https://travelata.ru/cookies-agreement',
    recommendationTechnologyUrl: 'https://travelata.ru/recommendation-technologies-agreement',
    privacyPolicyUrl: 'https://travelata.ru/privacy.html',
    personalDataAgreementUrl: 'https://travelata.ru/personal-data-visitor-consent.html',
    image: true,
  });

  const { isMobile } = useViewport();
  const showPopup = ref(false);
  const cookieService = new CookieService();

  const domain = computed<string>(() => {
    if (props.domain) {
      return props.domain;
    }

    const { hostname } = window.location;
    const parts = hostname.split('.');
    return `.${parts.slice(-2).join('.')}`;
  });

  const typoVariant = computed(() => (isMobile.value ? TypoVariant.BodyXSRegular : TypoVariant.BodyMRegular));

  const hasConsent = (): boolean => {
    const cookieValue = cookieService.get(props.cookieName);
    return cookieValue?.toString() === props.cookieValue;
  };

  const acceptCookies = (): void => {
    cookieService.set(props.cookieName, props.cookieValue, {
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
      path: '/',
      domain: domain.value,
    });

    showPopup.value = false;
  };

  onMounted(() => {
    if (!hasConsent()) {
      showPopup.value = true;
    }
  });
</script>

<template>
  <div
    v-if="showPopup"
    id="tl-cookie-consent"
    class="tl-cookie-consent"
    role="dialog"
    aria-label="Уведомление об использовании cookies"
    data-test-id="cookie-consent"
  >
    <div class="tl-cookie-consent__content">
      <div v-if="image" class="tl-cookie-consent__image">
        <Illustration name="cookies" />
      </div>
      <Typo :variant="typoVariant">
        Мы используем
        <a :href="cookiesUrl" rel="noopener noreferrer" target="_blank" class="tl-cookie-consent__link">файлы cookie</a>
        и
        <a
          :href="recommendationTechnologyUrl"
          rel="noopener noreferrer"
          target="_blank"
          class="tl-cookie-consent__link"
        >
          рекомендательные алгоритмы.
        </a>
        Продолжая использовать наш сайт, вы даете
        <a :href="personalDataAgreementUrl" rel="noopener noreferrer" target="_blank" class="tl-cookie-consent__link">
          согласие на обработку персональных данных
        </a>
        и принимаете
        <a :href="privacyPolicyUrl" rel="noopener noreferrer" target="_blank" class="tl-cookie-consent__link">
          политику обработки персональных данных.
        </a>
      </Typo>
    </div>
    <Button variant="primary" size="m" aria-label="Принять использование cookies" @click="acceptCookies">ОК</Button>
  </div>
</template>

<style lang="scss" scoped>
  .tl-cookie-consent {
    align-items: center;
    background: var(--color-neutral-0);
    border-radius: var(--spacing-xs);
    bottom: 24px;
    box-shadow: var(--shadow-s);
    display: flex;
    flex-direction: row;
    left: 2%;
    margin: 0 auto;
    max-width: 748px;
    padding: 16px;
    place-content: center space-between;
    position: fixed;
    right: 2%;
    z-index: 999;

    &__content {
      align-items: center;
      display: flex;
      flex-flow: row nowrap;
      gap: var(--spacing-m);
      justify-content: space-between;
      margin-right: 65px;
      padding: 2px 0;

      @media (max-width: $breakpoint-mobile-max) {
        gap: var(--spacing-xs);
        margin-right: var(--spacing-xxs);
        padding: 0;
      }
    }

    &__image {
      flex-shrink: 0;
      height: 39px;
      width: 39px;
    }

    &__link {
      color: var(--color-brand-secondary);
      text-decoration: none;
    }
  }
</style>
